import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';

const LABELS = {
  home: { es: 'Inicio', en: 'Home' },
  about: { es: 'Quiénes Somos', en: 'About Us' },
  directory: { es: 'Directorio', en: 'Directory' },
  membership: { es: 'Membresía', en: 'Membership' },
  news: { es: 'Noticias', en: 'News' },
  events: { es: 'Eventos', en: 'Events' },
  resources: { es: 'Recursos', en: 'Resources' },
  contact: { es: 'Contacto', en: 'Contact' },
};
const ORDER = ['home', 'about', 'directory', 'membership', 'news', 'events', 'resources', 'contact'];

export default function MenuVisibilityPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { fetchItems(); }, []);
  useEffect(() => {
    if (feedback) {
      const id = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(id);
    }
  }, [feedback]);

  async function fetchItems() {
    setLoading(true);
    try {
      const { data } = await api.get('/menus');
      const sorted = (data || []).sort((a, b) => ORDER.indexOf(a.menu_key) - ORDER.indexOf(b.menu_key));
      setItems(sorted);
    } catch {
      setFeedback({ type: 'error', message: 'Error al cargar los menús.' });
    } finally {
      setLoading(false);
    }
  }

  async function toggle(item) {
    setSaving(item.menu_key);
    const newVisible = !item.visible;
    try {
      await api.patch(`/menus/${item.menu_key}`, { visible: newVisible });
      setItems((prev) => prev.map((i) => (i.menu_key === item.menu_key ? { ...i, visible: newVisible } : i)));
      setFeedback({ type: 'success', message: `"${LABELS[item.menu_key]?.es || item.menu_key}" ${newVisible ? 'visible' : 'oculto'}.` });
    } catch {
      setFeedback({ type: 'error', message: `Error al actualizar "${LABELS[item.menu_key]?.es || item.menu_key}".` });
    } finally {
      setSaving(null);
    }
  }

  const visibleCount = items.filter((i) => i.visible).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-cmic-500" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="menu-visibility-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-charcoal-800">Visibilidad de Menús</h1>
        <p className="text-charcoal-500 mt-1">Controla qué secciones se muestran en el menú de navegación del sitio.</p>
      </div>

      {feedback && (
        <div
          data-testid="menu-feedback"
          className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-6 text-sm font-medium transition-all ${
            feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {feedback.message}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-charcoal-600">{visibleCount} de {items.length} menús visibles</span>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <span className="text-xs text-charcoal-400">Visible</span>
            <span className="w-2.5 h-2.5 rounded-full bg-charcoal-300 ml-3" />
            <span className="text-xs text-charcoal-400">Oculto</span>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {items.map((item) => {
            const label = LABELS[item.menu_key];
            const isSaving = saving === item.menu_key;
            return (
              <div
                key={item.menu_key}
                data-testid={`menu-row-${item.menu_key}`}
                className={`flex items-center justify-between px-6 py-4 transition-colors duration-200 ${item.visible ? 'bg-white' : 'bg-gray-50'}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.visible ? 'bg-emerald-50 text-emerald-600' : 'bg-charcoal-100 text-charcoal-400'}`}>
                    {item.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                  </div>
                  <div>
                    <p className={`font-medium ${item.visible ? 'text-charcoal-800' : 'text-charcoal-400'}`}>{label?.es || item.menu_key}</p>
                    <p className="text-xs text-charcoal-400">{label?.en || ''}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggle(item)}
                  disabled={isSaving}
                  data-testid={`menu-toggle-${item.menu_key}`}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cmic-500 focus:ring-offset-2 disabled:opacity-50 ${item.visible ? 'bg-emerald-500' : 'bg-charcoal-300'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${item.visible ? 'translate-x-6' : 'translate-x-1'}`} />
                  {isSaving && (<Loader2 size={12} className="absolute inset-0 m-auto animate-spin text-white" />)}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-charcoal-400 mt-4">
        Los cambios se aplican inmediatamente en el sitio público.
      </p>
    </div>
  );
}
